import { ApolloClient, ApolloLink, ApolloProvider, InMemoryCache, createHttpLink, from } from '@apollo/client';

import { onError } from '@apollo/client/link/error';

import APP_CONFIG from '@/_config/config.app';
import { useToast } from '@/hooks/use-toast.hook';
import { setContext } from '@apollo/client/link/context';
import { IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useMemo } from 'react';

type Props = {
  children: React.ReactNode;
};

const httpLink = createHttpLink({ uri: import.meta.env.VITE_GQL_URL });

const errorLink = (toast: (params: { description: string; variant: 'error' }) => void) =>
  onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, locations, path }) =>
        toast({
          description: `GQL error. ${operation.operationName} [GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          variant: 'error',
        }),
      );
    if (networkError) {
      toast({
        description: `Network error. ${operation.operationName} ${networkError}`,
        variant: 'error',
      });
    }
  });

const consoleLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((data) => {
    return data;
  });
});

const authLink = (instance: IPublicClientApplication) =>
  setContext(async (_, { headers }) => {
    await instance.initialize();
    const accounts = instance.getAllAccounts();

    const exp = accounts[0]?.idTokenClaims?.exp || 0;
    const currentTime = Math.floor(Date.now() / 1000);
    const timeLeft = exp - currentTime;
    const expired = timeLeft <= 60;

    const token = await instance
      .acquireTokenSilent({ ...APP_CONFIG.TOKEN_REQUEST, account: accounts[0], forceRefresh: expired })
      .catch(async () => {
        instance.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: '/login',
          onRedirectNavigate: () => {
            return false;
          },
        });
      });

    instance.setActiveAccount(accounts[0]);

    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token.accessToken}` : null,
      },
    };
  });

export const GraphQLContainer = ({ children }: Props) => {
  const { toast } = useToast();
  const { instance } = useMsal();

  const client = useMemo(() => {
    return new ApolloClient({
      link: from([consoleLink, errorLink(toast), authLink(instance), httpLink]),
      cache: new InMemoryCache({
        typePolicies: {
          Product: {
            fields: {
              tailoringProcess: {
                merge(existing, incoming, { mergeObjects }) {
                  return mergeObjects(existing, incoming);
                },
              },
            },
          },
          YesNoQuestion: {
            // due to neo4j returning same ids
            // we need to tell apollo how to identify the objects for caching
            keyFields: ['id', 'productId'],
          },
          ProductBuildingBlock: {
            // due to neo4j returning same ids
            // we need to tell apollo how to identify the objects for caching
            keyFields: ['id', 'productId'],
          },
        },
      }),
    });
  }, []);
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
